import { useEffect, useState, useCallback, useRef } from 'react';
import './styles.scss';
import { useSelector, useDispatch } from 'react-redux';
import { PlusIcon, FadersHorizontalIcon } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { updateHeader } from '../../slices/header-slice';
import { updateBasesData } from '../../slices/app-slice';
import { getBases } from '../../api';
import { useDataLoading } from '../../hooks/useDataLoading';

import { LazyLoadedGroup } from '../../components/lazy-loaded-group';
import { groupArrayInChunks, createSlug } from '../../utility/utils';
import FilterOverlay from '../../components/filter-overlay';


const Bases = ({ type }) => {
    const navigate = useNavigate();
    const bases = useSelector((state) => state.app.bases);
    const announcement = useSelector((state) => state.app.homepage?.announcement);
    const dispatch = useDispatch();

    const [filters, setFilters] = useState([]);
    const [activeFilter, setActiveFilter] = useState(null);
    const [activeType, setActiveType] = useState(null);
    const [filterResults, setFilterResults] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);

    const [isBasesLoading, setIsBasesLoading] = useState(false);
    const hasFetched = useRef(false);

    useDataLoading('bases', isBasesLoading, []);

    useEffect(() => {
        const fetchBasesData = async () => {
            const hasData = bases && Array.isArray(bases) && bases.length > 0;

            if (!hasData && !hasFetched.current) {
                hasFetched.current = true;
                setIsBasesLoading(true);
                try {
                    const response = await getBases();
                    dispatch(updateBasesData(response.data));
                } catch (error) {
                    console.error('Error fetching bases data:', error);
                } finally {
                    setIsBasesLoading(false);
                }
            }
        };

        fetchBasesData();
    }, [bases, dispatch]);

    // Build filter configuration for FilterOverlay
    const filterConfig = {
        material: {
            label: 'Material',
            type: 'radio',
            options: filters.map(filter => ({
                value: filter,
                label: filter
            }))
        },
        type: {
            label: 'Types',
            type: 'radio',
            options: [
                { value: 'Coffee Table', label: 'Coffee Table' },
                { value: 'Console Table', label: 'Console Table' },
                { value: 'Dining Table', label: 'Dining Table' }
            ]
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Set filters and initialize results
    useEffect(() => {
        dispatch(updateHeader('light'));

        if (bases && Array.isArray(bases) && bases.length > 0) {
            const categories = bases.filter(b => b?.material !== null && b?.material !== undefined).map(x => x.material);
            const unique = Array.from(new Set(categories));
            setFilters(unique);
            setFilterResults(groupArrayInChunks(bases, 8));
        }
    }, [dispatch, bases, type]);

    useEffect(() => {
        if (!bases || !Array.isArray(bases) || bases.length === 0) {
            setFilterResults([]);
            return;
        }

        let result = [...bases];

        if (activeFilter) {
            result = result.filter(x => x?.material === activeFilter);
        }

        if (activeType) {
            result = result.filter(x => x?.type === activeType);
        }

        setFilterResults(groupArrayInChunks(result, 8));
    }, [activeFilter, activeType, bases]);


    const handleFilterApply = useCallback((appliedFilters) => {
        setActiveFilter(appliedFilters.material || null);
        setActiveType(appliedFilters.type || null);
    }, []);

    const onProductClick = (item) => {
        if (!item?.id || !item?.title) return;

        const titleSlug = createSlug(item.title);
        navigate({
            pathname: `/bases/${item.id}`,
            search: `?title=${titleSlug}`,
        });
    };

    return (
        <div className={`bases-page ${announcement ? 'has-announcement' : ''}`}>
            <div className="store-intro">
                <h1>Base Designs.</h1>
                <p>All bases designs are proprietary designs & can only be found here.</p>
                <p>Looking for something truly one of a kind? <a className='text-link' href="/contact">Send us a message</a> & our designers can create a never before seen base just for you</p>
            </div>

            {/* <div className="base-type-filter">
                <div className="base-filter info">
                    <div>
                        <h2>Base Type</h2>
                        <p>Select to filter design choice.</p>
                    </div>
                </div>
                {['Dining Table', 'Coffee Table', 'Console Table'].map((type) => (
                    <div
                        key={type}
                        className={`base-filter ${activeBaseFilter === type ? "active" : ""}`}
                        onClick={() => setActiveBaseFilter(activeBaseFilter === type ? null : type)}
                    >
                        <img src={
                            type === 'Dining Table' ? baseFilterDining :
                                type === 'Coffee Table' ? baseFilterCoffee :
                                    baseFilterConsole
                        } alt={`${type} Filter`} />

                    </div>
                ))}
            </div> */}

            <div className="tools-wrap">
                <div className="tools">
                    <div className="filter" onClick={() => setFilterOpen(!filterOpen)}>
                        <span><span> Filters <PlusIcon size={16} /> </span><span>Sort <FadersHorizontalIcon size={24} /></span></span>
                    </div>
                </div>
            </div>

            <FilterOverlay
                isOpen={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                filters={filterConfig}
                activeFilters={{
                    material: activeFilter,
                    type: activeType
                }}
                title="BASES"
            />

            <div className="lazy-load">
                {filterResults.map((group, i) => (
                    <LazyLoadedGroup
                        key={i}
                        group={group}
                        index={i}
                        type={type}
                        onProductClick={onProductClick}
                    />
                ))}
            </div>
        </div>
    );
};

export default Bases;
